import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import Logo from "@/components/Logo";
import InstallAppButton from "@/components/InstallAppButton";
import {
  LayoutDashboard, Users, FileText, Package, Truck, Receipt,
  Wallet, Building2, Boxes, Beaker, MessageSquareWarning, Settings2,
  BarChart3, LogOut, Coins, IndianRupee, Factory, Menu, X,
} from "lucide-react";

const SECTIONS = {
  admin: [
    { title: "Home", items: [{ to: "/", label: "Action Board", icon: LayoutDashboard }]},
    { title: "Sales", items: [
      { to: "/leads", label: "Leads", icon: Users },
      { to: "/quotes", label: "Quotes", icon: FileText },
      { to: "/customers", label: "Customers", icon: Users },
      { to: "/samples", label: "Samples", icon: Beaker },
    ]},
    { title: "Production", items: [
      { to: "/orders", label: "Orders", icon: Package },
      { to: "/floor", label: "Floor View", icon: Factory },
      { to: "/dispatch", label: "Dispatch", icon: Truck },
      { to: "/delivery-route", label: "Route Sheet", icon: Truck },
      { to: "/vendors", label: "Vendors", icon: Building2 },
      { to: "/products", label: "Products", icon: Boxes },
    ]},
    { title: "Financial", items: [
      { to: "/invoices", label: "Invoices", icon: Receipt },
      { to: "/payments", label: "Payments", icon: IndianRupee },
      { to: "/expenses", label: "Expenses", icon: Wallet },
      { to: "/cash", label: "Cash Log", icon: Coins },
    ]},
    { title: "Insights", items: [
      { to: "/reports", label: "Reports", icon: BarChart3 },
      { to: "/reconciliation", label: "Reconciliation", icon: BarChart3 },
    ]},
    { title: "Support", items: [
      { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
      { to: "/settings", label: "Settings", icon: Settings2 },
    ]},
  ],
  team: [
    { title: "Home", items: [{ to: "/", label: "Action Board", icon: LayoutDashboard }]},
    { title: "Sales", items: [
      { to: "/leads", label: "Leads", icon: Users },
      { to: "/quotes", label: "Quotes", icon: FileText },
      { to: "/customers", label: "Customers", icon: Users },
      { to: "/samples", label: "Samples", icon: Beaker },
    ]},
    { title: "Production", items: [
      { to: "/orders", label: "Orders", icon: Package },
      { to: "/floor", label: "Floor View", icon: Factory },
      { to: "/dispatch", label: "Dispatch", icon: Truck },
      { to: "/vendors", label: "Vendors", icon: Building2 },
      { to: "/products", label: "Products", icon: Boxes },
    ]},
    { title: "Financial", items: [
      { to: "/invoices", label: "Invoices", icon: Receipt },
      { to: "/expenses", label: "Expenses", icon: Wallet },
    ]},
    { title: "Support", items: [
      { to: "/complaints", label: "Complaints", icon: MessageSquareWarning },
    ]},
  ],
  sales: [
    { title: "Home", items: [{ to: "/", label: "Action Board", icon: LayoutDashboard }]},
    { title: "Sales", items: [
      { to: "/leads", label: "Leads", icon: Users },
      { to: "/quotes", label: "Quotes", icon: FileText },
      { to: "/customers", label: "Customers", icon: Users },
      { to: "/samples", label: "Samples", icon: Beaker },
    ]},
  ],
  production: [
    { title: "Home", items: [{ to: "/", label: "Action Board", icon: LayoutDashboard }]},
    { title: "Production", items: [
      { to: "/orders", label: "Orders", icon: Package },
      { to: "/floor", label: "Floor View", icon: Factory },
      { to: "/dispatch", label: "Dispatch", icon: Truck },
      { to: "/vendors", label: "Vendors", icon: Building2 },
      { to: "/products", label: "Products", icon: Boxes },
    ]},
  ],
  cash: [
    { title: "Cash", items: [{ to: "/cash", label: "Cash Log", icon: Coins }]},
  ],
};

function SidebarNav({ sections, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto pr-1">
      {sections.map((sec, sidx) => (
        <div key={sec.title} className={sidx === 0 ? "mb-2" : "mt-3 mb-2"}>
          {sec.title !== "Home" && (
            <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#8B857B] font-display font-semibold">
              {sec.title}
            </div>
          )}
          <div className="space-y-0.5">
            {sec.items.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g,"-")}`}
                className={({ isActive }) => (isActive ? "active" : "")}>
                <Icon size={16} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sections = SECTIONS[user?.role] || [];

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const currentLabel = (() => {
    for (const sec of sections) {
      for (const it of sec.items) {
        if (it.to === loc.pathname || (it.to !== "/" && loc.pathname.startsWith(it.to))) return it.label;
      }
    }
    return loc.pathname === "/" ? "Action Board" : "";
  })();

  return (
    <div className="min-h-screen flex">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 bg-[#242321] text-white border-b border-black/30" data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(true)} className="p-1.5 -ml-1 rounded hover:bg-white/10" data-testid="mobile-menu-btn" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <Logo light size={18} />
          <span className="text-xs text-[#C6C2BB] font-display font-semibold">·</span>
          <span className="text-sm font-display font-semibold">{currentLabel}</span>
        </div>
        <div className="w-8" />
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="sidebar relative w-64 h-full flex flex-col p-3 shadow-2xl animate-slide-in-left">
            <div className="flex items-center justify-between px-2 py-3 mb-2">
              <Logo light size={22} />
              <button onClick={() => setMobileOpen(false)} className="p-1 rounded text-[#C6C2BB] hover:text-white hover:bg-white/10" aria-label="Close menu">
                <X size={20} />
              </button>
            </div>
            <SidebarNav sections={sections} onNavigate={() => setMobileOpen(false)} />
            <div className="mt-3 pt-3 border-t border-[#363431] px-2 space-y-2">
              <InstallAppButton variant="ghost" className="w-full justify-center text-[#FDFBF7] border-[#4a4844] hover:bg-white/10" />
              <div className="text-xs text-[#A3A09A] mb-1">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A09A] mb-2">{user?.role}</div>
              <button onClick={async () => { await logout(); nav("/login"); }}
                className="flex items-center gap-2 text-xs text-[#C6C2BB] hover:text-white">
                <LogOut size={13} /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="sidebar w-56 shrink-0 hidden md:flex md:flex-col p-3 sticky top-0 h-screen">
        <div className="px-2 py-3 mb-2">
          <Logo light size={24} />
        </div>
        <SidebarNav sections={sections} />
        <div className="mt-3 pt-3 border-t border-[#363431] px-2 space-y-2">
          <InstallAppButton variant="ghost" className="w-full justify-center text-[#FDFBF7] border-[#4a4844] hover:bg-white/10" />
          <div className="text-xs text-[#A3A09A] mb-1">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#A3A09A] mb-2">{user?.role}</div>
          <button onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-xs text-[#C6C2BB] hover:text-white">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="p-3 md:p-8 max-w-full md:max-w-[1400px] pt-16 md:pt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
